import { TResult, makeSuccess } from "../../sg-core/utils/Result";
import { ISgApiService } from "../../sg-core/services/ApiService2";
import IFormType from "../interfaces/IFormType";
import ICategory from "../interfaces/ICategory";
import IFormDefinition from "../interfaces/IFormDefinition";
import FormDefinitionMapper from "../mappers/FormDefinitionMapper";
import FormTypeMapper from "../mappers/FormTypeMapper";
import CategoryMapper from "../mappers/CategoryMapper";
import PermissionMapper from "../mappers/PermissionMapper";
import { SemanticICONS } from "semantic-ui-react/dist/commonjs/generic";
import { IPermissionDTO, IAppRoleDTO, ICategoryPermission } from "../interfaces/ICategoryPermission";
import { IRole } from "../interfaces/IAuditoinnitUser";
import { RoleMapper } from "../mappers/RoleMapper";
import { IEndpoint } from "../../sg-core/services/EndpointService";
import { IPerson } from "../../sg-hpr/models/IPerson";
import { AppParameterMapper, IAppParatemers, IAppParatemersDTO } from "../interfaces/IAppParatemers";
import { AppConfigurationMapper, IAppConfigurations, IAppConfigurationsDTO } from "../interfaces/AppConfigurations";

export interface IInitialContextData {
    formTypes: IFormType[]
    categores: ICategory[]
    persons: IPerson[]
    categoryPermissions: ICategoryPermission[];
    appRoles: IRole[];
    appParamters: IAppParatemers | null;
    appConfigs: IAppConfigurations | null;
}

export interface IContextService {
    getInitialData(): Promise<TResult<IInitialContextData>>;
    getFormDefinition(formId: number): Promise<TResult<IFormDefinition>>;
    getUserAppRoles: () => Promise<TResult<any>>;
    getAnonymousForm: (guid: string) => Promise<TResult<{ formTypeId: number; token: string }>>;
    getFormType: (id: IFormType["id"]) => Promise<TResult<IFormType>>;
}

export interface IInitialContextResult {
    success: true;
    // action: "hae_perustiedot";
    henkilot: { id: number; nimi: string; kuva: string, etunimi: string; sukunimi: string; }[];
    lomakkeet: { id: number; guid: string; nimi: string; kategoria: number; lomakemaaritys: number; tyonkulku: number; viitenimi?: string }[];
    kategoriat: { id: number; nimi: string; ikoni?: SemanticICONS }[];
    oikeustasot?: IPermissionDTO[];
    roolit?: IAppRoleDTO[];
    parametrit: IAppParatemersDTO;
    config: IAppConfigurationsDTO;
}

export interface IFormConfigResult {
    success: true;
    action: "hae_perustiedot";
    lomakemaaritys: { id: number; nimi: string; kategoria: number; tyonkulku: number; data: string };
    lomake: { id: number; nimi: string; kategoria: number; tyonkulku: number; lomakemaaritys: { data: string } };
}


export const getContextService = (apiService: ISgApiService, Enpoint: IEndpoint, anonymousSession: boolean): IContextService => {

    const makeAnonymousAction = (actionName: string) => anonymousSession ? `${actionName}_anonyymi` : actionName;

    const ensureArray = <T>(v: T[]): T[] => {
        if (!v || !Array.isArray(v)) {
            return [];
        }
        return v;
    };

    const getInitialData = async (): Promise<TResult<IInitialContextData>> => {
        const action = makeAnonymousAction("hae_perustiedot");
        const apiResponse = await apiService.sendAction<IInitialContextResult>(Enpoint, action, {});
        if (apiResponse.isSuccess === true) {
            return makeSuccess<IInitialContextData>({
                formTypes: ensureArray(apiResponse.value.lomakkeet).map(FormTypeMapper.apiToDomain),
                categores: ensureArray(apiResponse.value.kategoriat).map(CategoryMapper.apiToDomain),
                // locations: apiResponse.value.sijainnit.map(LocationMapper.apiToDomain),
                // facilities: apiResponse.value.kohteet.map(FacilityMapper.apiToDomain),
                // areas: apiResponse.value.alueet.map(AreaMapper.apiToDomain),
                persons: [],
                categoryPermissions: PermissionMapper.permissionsToDomain(apiResponse.value.oikeustasot || []),
                appRoles: apiResponse.value.roolit ? apiResponse.value.roolit.map(RoleMapper.apiToDomain) : [],
                appParamters: apiResponse.value.parametrit ? AppParameterMapper.apiToDomain(apiResponse.value.parametrit) : null,
                appConfigs: apiResponse.value.config ? AppConfigurationMapper.apiToDomain(apiResponse.value.config) : null
            });
        }
        return apiResponse
    };



    const getUserAppRoles = async (): Promise<TResult<any>> => {
        const action = makeAnonymousAction("hae_kayttajaroolit");
        const body = {}
        const apiResponse = await apiService.sendAction<any>(Enpoint, action, body);
        if (apiResponse.isSuccess === true) {
            return makeSuccess(apiResponse.value.roolilista.split(","));
        }
        return apiResponse
    };


    const getAnonymousForm = async (guid: string): Promise<TResult<{ formTypeId: number, token: string }>> => {
        const action = "kasittele_lomakeavaus_anonyymi"
        const body = { lomake: { guid } }
        const apiResponse = await apiService.sendAction<any>(Enpoint, action, body, true);
        if (apiResponse.isSuccess === true) {
            return makeSuccess({
                formTypeId: apiResponse.value.lomake.id,
                token: apiResponse.value.sid
            });
        }
        return apiResponse
    };

    const getFormDefinition = async (formDefinitionId: number): Promise<TResult<IFormDefinition>> => {
        const action = "hae_lomakemaaritys"
        const body = { lomakemaaritys: { id: formDefinitionId } }
        const apiResponse = await apiService.sendAction<IFormConfigResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess(FormDefinitionMapper.apiToDomain(apiResponse.value.lomakemaaritys));
        }
        return apiResponse
    };

    const getFormType: IContextService["getFormType"] = async (id): Promise<TResult<IFormType>> => {
        const action = "hae_lomake";
        const body = { lomake: { id } }
        const apiResponse = await apiService.sendAction<{ lomake: IInitialContextResult["lomakkeet"][0] }>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess(FormTypeMapper.apiToDomain(apiResponse.value.lomake));
        }
        return apiResponse
    };


    return {
        getInitialData,
        getFormDefinition,
        getUserAppRoles,
        getAnonymousForm,
        getFormType
    };
};
